// Delivery details the buyer adds to a FALSE_DELIVERY incident, for the carrier.
// Mirrors platform's ShippingIncidents::RecipientDeliveryDetails, which is the source of truth:
// keep the limits, required fields, phone rule, hosts and path rules below in sync with it.

export const RECIPIENT_DETAILS_MAX_LENGTHS = {
    address_details: 250,
    between_streets: 100,
    contact_phone: 25,
    additional_contact_phone: 25,
    address_references: 250,
    maps_link: 500,
}

export const RECIPIENT_DETAILS_FIELDS = Object.keys(RECIPIENT_DETAILS_MAX_LENGTHS)
export const REQUIRED_DETAILS = ['address_details', 'between_streets', 'contact_phone', 'address_references']
const PHONE_FIELDS = ['contact_phone', 'additional_contact_phone']
// The description is the delivery discrepancy: required with the details, and capped like them.
export const DESCRIPTION_MAX_LENGTH = 500

// Digits with the usual separators and an optional leading "+", at most 15 digits (E.164). The
// minimum is a local number with its area code in the order's country (Chile has 9 digits);
// 8 elsewhere, where a "+" and country code may be needed. Mirrors platform's PHONE_MIN_DIGITS.
const PHONE_CHARACTERS = /^\+?[0-9 ().-]+$/
const PHONE_MAX_DIGITS = 15
const PHONE_MIN_DIGITS = { MX: 10, BR: 10, CO: 10, US: 10, CL: 9 }
const PHONE_DEFAULT_MIN_DIGITS = 8

// Only Maps paths are accepted on Google hosts: any other path there can be Google's /url
// redirector.
//   SHORT_LINK_HOSTS  any path (Maps share links)
//   MAPS_HOSTS        "/" (e.g. /?q=lat,lng) or /maps…
//   MAPS_PATH_HOSTS   /maps… only
const SHORT_LINK_HOSTS = ['maps.app.goo.gl']
const MAPS_HOSTS = [
    'maps.google.com', 'maps.google.com.mx', 'maps.google.com.br', 'maps.google.com.co', 'maps.google.cl',
]
const MAPS_PATH_HOSTS = [
    'goo.gl',
    'google.com', 'www.google.com',
    'google.com.mx', 'www.google.com.mx',
    'google.com.br', 'www.google.com.br',
    'google.com.co', 'www.google.com.co',
    'google.cl', 'www.google.cl',
]

// The backend parses links strictly (RFC 3986), so anything it would reject is rejected here as
// well rather than failing on submit: only URI characters and well-formed %XX escapes, which
// rules out spaces, accents, backslashes, | { } ^ ` " < > and [ ].
const URI_CHARACTERS = /^(?:[A-Za-z0-9\-._~:/?#@!$&'()*+,;=]|%[0-9A-Fa-f]{2})+$/
// Scheme, then the raw authority (before the browser decodes it) and the raw path (before the
// browser resolves dot segments in it).
const RAW_PARTS = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/([^/?#]*)([^?#]*)/

const isDotSegment = (segment) => {
    try {
        return ['.', '..'].includes(decodeURIComponent(segment))
    } catch {
        return true
    }
}

export function isValidMapsLink(value) {
    if (typeof value !== 'string' || !URI_CHARACTERS.test(value) || value.split('#').length > 2) return false
    const raw = value.match(RAW_PARTS)
    if (!raw || raw[1].includes('%')) return false
    const rawPath = raw[2]
    // Browsers resolve dot segments (/maps/../url is /url), so they would escape the path rule.
    if (rawPath.split('/').some(isDotSegment)) return false

    let url
    try {
        url = new URL(value)
    } catch {
        return false
    }
    if (url.protocol !== 'https:' || url.username || url.password || url.port || !url.hostname) return false

    const host = url.hostname.toLowerCase()
    if (SHORT_LINK_HOSTS.includes(host)) return true

    const mapsPath = rawPath === '/maps' || rawPath.startsWith('/maps/')
    if (MAPS_HOSTS.includes(host)) return mapsPath || rawPath === '' || rawPath === '/'
    return MAPS_PATH_HOSTS.includes(host) && mapsPath
}

// The backend's clean-up of every detail: control and invisible format characters (LRM,
// zero-width space, ... which chat apps add around pasted links and numbers) become spaces,
// whitespace collapses. Validate and send this, not the raw input: trim() keeps those characters
// and the link or phone would be rejected here while the backend accepts it.
export function cleanDetail(value) {
    if (typeof value !== 'string') return ''
    return value.replace(/[\p{Cc}\p{Cf}]/gu, ' ').replace(/\s+/g, ' ').trim()
}

// Free text also loses the " | " separator and the characters that read like it (a link keeps
// them, so it is rejected instead).
export function normalizeDetail(value) {
    return cleanDetail(typeof value === 'string' ? value.replace(/[|｜│┃¦∣ǀ❘⏐︱︳׀]/g, '/') : value)
}

// country: the order's shipping country (alpha-2).
export function isValidPhone(value, country) {
    if (typeof value !== 'string') return false
    const phone = value.replace(/\s+/g, ' ').trim()
    const digits = phone.replace(/[^0-9]/g, '').length
    const minDigits = PHONE_MIN_DIGITS[String(country ?? '').trim().toUpperCase()] ?? PHONE_DEFAULT_MIN_DIGITS
    return PHONE_CHARACTERS.test(phone) && digits >= minDigits && digits <= PHONE_MAX_DIGITS
}

// Maps "Share" can paste a place name and address around the link; keep just the link. Text
// that already starts with the link is left as typed: this runs on every keystroke, and cutting
// "…/AbC C" back to the link would eat each letter after a space and glue the rest onto the
// path. A note typed after the link stays visible and fails validation instead.
export function extractMapsLink(text) {
    if (typeof text !== 'string' || !/\s/.test(text.trim()) || /^https:\/\//.test(text.trim())) return text
    const match = text.match(/https:\/\/\S+/)
    return match ? match[0] : text
}

// Returns { field: errorCode } for what the backend would reject: a missing required field
// (RECIPIENT_DETAILS_REQUIRED, which only disables submit) or an invalid filled-in one.
export function recipientDetailsErrors(inputs, country) {
    const errors = {}
    RECIPIENT_DETAILS_FIELDS.forEach(field => {
        const value = cleanDetail(inputs?.[field])
        if (!value) {
            if (REQUIRED_DETAILS.includes(field)) errors[field] = 'RECIPIENT_DETAILS_REQUIRED'
            return
        }
        if (value.length > RECIPIENT_DETAILS_MAX_LENGTHS[field]) errors[field] = 'RECIPIENT_DETAILS_TOO_LONG'
        else if (PHONE_FIELDS.includes(field) && !isValidPhone(value, country)) errors[field] = 'INVALID_PHONE'
        else if (field === 'maps_link' && !isValidMapsLink(value)) errors[field] = 'INVALID_MAPS_LINK'
    })
    const description = normalizeDetail(inputs?.description)
    if (!description) errors.description = 'RECIPIENT_DETAILS_REQUIRED'
    else if (description.length > DESCRIPTION_MAX_LENGTH) errors.description = 'RECIPIENT_DETAILS_TOO_LONG'
    return errors
}
